import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { catalogApi } from '@/lib/catalog-api';
import { type Cell, type Plan, PLANS, type Reads } from '@/modules/marketing/pricing-data';
import type { FeatureLimits, Product } from '@/types/api';

/**
 * The plans, prices and allowances as the catalogue has them right now.
 *
 * ## Why the page does not simply state them
 *
 * It used to. `pricing-data.ts` carried the prices and the limits, and the admin catalogue carried
 * the ones the platform enforced — so raising Starter from five servers to twenty changed what
 * customers got and left the public page promising five, until somebody remembered to edit the code
 * and deploy. Two copies of a price is the worst pair of copies a site can have.
 *
 * Now the static values are the *fallback*: they are what the pre-rendered HTML carries, so a
 * crawler and the first paint see something true as of the build, and the live catalogue replaces
 * them the moment it answers.
 *
 * ## Matched by interval and order, never by slug
 *
 * Plan slugs differ between installations (`starter-monthly` in development, `starter` in
 * production), so nothing here names one. The monthly plans of the published products, in the
 * catalogue's own order, are the columns; their yearly counterparts are matched by name, since a
 * yearly plan is the same plan billed differently.
 */

export interface LivePricing {
  plans: Plan[];
  /** Entitlements per column, in the same order as `plans`. */
  limits: FeatureLimits[];
  trialDays: number;
  trialLimits: FeatureLimits;
}

interface TrialTerms {
  available: boolean;
  days: number;
  features: FeatureLimits;
}

/** A yearly plan is the monthly one billed differently: "Starter (yearly)" belongs to "Starter". */
function baseName(name: string): string {
  return name.replace(/\s*\((yearly|annual|monthly)\)\s*$/i, '').trim();
}

function build(products: Product[], trial: TrialTerms): LivePricing | null {
  const plans = products
    .filter((product) => product.status === 'active')
    .flatMap((product) => product.plans)
    .filter((plan) => plan.status === 'active' && !plan.trial);

  const monthly = plans
    .filter((plan) => plan.billing_interval === 'monthly')
    .sort((a, b) => a.sort_order - b.sort_order);

  if (monthly.length === 0) {
    return null;
  }

  return {
    plans: monthly.map((plan) => {
      // A plan the page already has words for keeps them: the catalogue's description is an
      // operator's note ("Billed monthly. Cancel at any time."), not the line that sells the plan.
      // A plan added in the catalogue and unknown here falls back to its own description.
      const written = PLANS.find((known) => known.name === baseName(plan.name));

      return {
        name: baseName(plan.name),
        monthly: plan.price_minor / 100,
        blurb: written?.blurb ?? plan.description,
        cta: written?.cta ?? `Choose ${baseName(plan.name)}`,
        // The middle column of three, as the static table has always highlighted it.
        highlighted: monthly.length === 3 && plan === monthly[1],
        // A yearly plan may be priced independently of twelve monthlies; when there is one, its
        // own price is what the yearly view shows.
        yearly:
          plans.find(
            (other) => other.billing_interval === 'yearly' && baseName(other.name) === baseName(plan.name),
          )?.price_minor ?? null,
      };
    }),
    limits: monthly.map((plan) => plan.features),
    trialDays: trial.days,
    trialLimits: trial.features,
  };
}

/**
 * Null until the catalogue answers, and null for good if it cannot: the page then keeps the values
 * it was built with rather than showing a table with holes in it.
 */
export function useLivePricing(): LivePricing | null {
  const { data } = useQuery({
    queryKey: ['catalogue', 'pricing'],
    queryFn: async (): Promise<LivePricing | null> => {
      const [products, trial] = await Promise.all([
        catalogApi.listProducts(),
        apiClient.get<TrialTerms>('/catalog/trial', { skipAuth: true }),
      ]);

      return build(products, trial);
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return data ?? null;
}

/**
 * One row's cells, from the catalogue where it is the authority and from the page's own values
 * otherwise.
 *
 * A feature a plan does not carry is not guessed at: the built-in value stands, because an absent
 * key means the plan was written before the feature existed, not that the plan grants none of it.
 */
export function liveCells(
  row: { trial: Cell; plans: Cell[]; feature?: string; reads?: Reads },
  live: LivePricing | null,
): { trial: Cell; plans: Cell[] } {
  if (live === null || row.feature === undefined || row.reads === undefined) {
    return { trial: row.trial, plans: row.plans };
  }

  const cell = (limits: FeatureLimits, fallback: Cell): Cell => {
    const value = limits[row.feature as string];

    if (value === undefined) {
      return row.reads === 'yesno' ? false : fallback;
    }

    if (row.reads === 'yesno') {
      return value === true || (typeof value === 'number' && value !== 0);
    }

    if (typeof value !== 'number') {
      return fallback;
    }

    if (value === -1) {
      return 'Unlimited';
    }

    return row.reads === 'people' && value <= 1 ? 'Just you' : String(value);
  };

  return {
    trial: cell(live.trialLimits, row.trial),
    plans: live.plans.map((_, index) => cell(live.limits[index] ?? {}, row.plans[index] ?? '')),
  };
}
